import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Alert, AlertDescription, AlertTitle } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { InfoIcon } from "lucide-react";
import { useAuditPlansQuery } from "../api/queries";
import { AuditPlanFilter } from "../ui/AuditPlanFilter";
import { AuditPlanTable } from "../ui/AuditPlanTable";

export function AuditRecordNewPage() {
  const navFromRoute = useNavigate({ from: "/app/internal-audit/records/new" });
  const nav = useNavigate();
  const search = useSearch({
    from: "/app/internal-audit/records/new",
  }) as OffsetPageQueryDTO & {
    planId?: string;
  };

  const list = useAuditPlansQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const handleSelectPlan = (plan: { id: string }) => {
    // Navigate to create record form with selected audit plan
    nav({
      to: "/app/internal-audit/records/new/$planId",
      params: { planId: plan.id },
    });
  };

  return (
    <>
      <Header />

      <Main>
        <div className="mb-4">
          <h2 className="font-bold text-2xl tracking-tight">
            ສ້າງບັນທຶກການກວດສອບໃໝ່
          </h2>
          <p className="text-muted-foreground">ເລືອກຜະແນການກວດສອບເພື່ອສ້າງບັນທຶກ</p>
        </div>

        <Alert className="mb-4">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>ກະລຸນາເລືອກຜະແນການກວດສອບ</AlertTitle>
          <AlertDescription>
            ກະລຸນາເລືອກຜະແນການກວດສອບທີ່ທ່ານຕ້ອງການສ້າງບັນທຶກການກວດສອບ
          </AlertDescription>
        </Alert>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <AuditPlanFilter
            routePath="/app/internal-audit/records/new"
            preservePlanId
          />

          <AuditPlanTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              navFromRoute({
                search: {
                  offset,
                  limit,
                  sort: search.sort,
                  filters: search.filters,
                  planId: search.planId as string | undefined,
                },
              })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              navFromRoute({
                search: {
                  offset: search.offset ?? 0,
                  limit: search.limit ?? 20,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  filters: search.filters,
                  planId: search.planId as string | undefined,
                },
              })
            }
            onEdit={(plan) =>
              nav({
                to: "/app/internal-audit/$id/edit",
                params: { id: plan.id },
              })
            }
            onView={(plan) =>
              nav({
                to: "/app/internal-audit/$id",
                params: { id: plan.id },
              })
            }
            onSelect={handleSelectPlan}
          />
        </div>
      </Main>
    </>
  );
}
